import './index.css';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useMemo, useState, useRef } from 'react';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { Input } from '@hh.ru/magritte-ui-input';
import { RegionSelect } from './RegionSelect.js';
import { phoneFormatterFactory } from './phoneFormatter.js';
import { useCountrySelectDataProvider } from './useCountrySelectDataProvider.js';
import { useLibPhoneNumber } from './useLibPhoneNumber.js';
import { s as styles } from './CallingCodeInput-Bszjh-Og.js';
import 'classnames';
import '@hh.ru/magritte-common-flag-emoji-font';
import '@hh.ru/magritte-ui-cell';
import '@hh.ru/magritte-ui-select';
import '@hh.ru/magritte-common-data-provider';
import './basicMetadata.js';
import '@hh.ru/magritte-ui-breakpoint';

const DEFAULT_PRIORITY_COUNTRIES = ['RU', 'BY', 'KZ', 'UZ'];
const notDigitsRegExp = /[\D]/g;
const PhoneInput = forwardRef(({ defaultCountry = 'RU', defaultNationalNumber = '', onChange, availableCountries, invalid, description, errorMessage, priorityCountries = DEFAULT_PRIORITY_COUNTRIES, onFocus, onBlur, countryNames, priorityCountriesTitle, mobileOnly, 'data-qa': dataQa = 'magritte-phone-input-wrapper', inputsWrapperRef, phoneInputElementRef, codeInputElementRef, onPhoneValidationApiLoadError, disabled, }, ref) => {
    const metadataVariant = useMemo(() => {
        const minimalMetadata = availableCountries?.length === DEFAULT_PRIORITY_COUNTRIES.length &&
            DEFAULT_PRIORITY_COUNTRIES.every((country) => availableCountries.includes(country));
        if (minimalMetadata) {
            return 'ru-by-kz-uz';
        }
        const keys = Object.keys(countryNames);
        if (keys.length === DEFAULT_PRIORITY_COUNTRIES.length &&
            DEFAULT_PRIORITY_COUNTRIES.every((country) => keys.includes(country))) {
            return 'ru-by-kz-uz';
        }
        return 'max';
    }, [availableCountries, countryNames]);
    const libPhoneNumberAPI = useLibPhoneNumber(metadataVariant, !!mobileOnly, onPhoneValidationApiLoadError);
    const { getCountryCallingCode, isValidPhoneNumber, isLoaded } = libPhoneNumberAPI;
    const [country, setCountry] = useState(defaultCountry);
    const countryRef = useRef(country);
    const countryCallingCodeRef = useRef(getCountryCallingCode(country));
    const focusInsideRef = useRef(false);
    const wrapperRef = useRef(null);
    const wrapperRefMultiplexer = useMultipleRefs(wrapperRef, inputsWrapperRef);
    const [phoneValue, setPhoneValue] = useState(defaultNationalNumber);
    const phoneInputRef = useRef(null);
    const inputRefMultiplexer = useMultipleRefs(phoneInputRef, phoneInputElementRef);
    const forceDisabled = useDisabled();
    const isComponentDisabled = forceDisabled || !!disabled;
    countryRef.current = country;
    countryCallingCodeRef.current = getCountryCallingCode(country);
    const { dataProvider: selectDataProvider, options: selectOptions } = useCountrySelectDataProvider({
        availableCountries,
        countryNames,
        priorityCountriesTitle,
        priorityCountries,
        libPhoneNumberAPI,
    });
    const phoneFormatter = useMemo(() => {
        return phoneFormatterFactory({
            // обернуто в промис т.к. форматтер вызывается в процессе рендера BaseInput и не может синхронно
            // вызывать изменения стейта другого компонента
            setCountry: (country) => Promise.resolve().then(() => setCountry(country)),
            currentCountryRef: countryRef,
            libPhoneNumberAPI,
        });
    }, [libPhoneNumberAPI]);
    const onChangeHandler = (country, phoneValue) => {
        const callingCode = getCountryCallingCode(country);
        const nationalNumber = phoneValue.replace(notDigitsRegExp, '');
        onChange({
            country,
            callingCode,
            phone: `+${callingCode}${nationalNumber}`,
            nationalNumber,
            isValid: isValidPhoneNumber(nationalNumber, country),
            isPhoneValidationApiLoaded: isLoaded,
        });
    };
    const focusHandler = () => {
        if (focusInsideRef.current) {
            return;
        }
        focusInsideRef.current = true;
        onFocus?.();
    };
    const blurHandler = () => {
        requestAnimationFrame(() => {
            if (wrapperRef.current?.contains(document.activeElement)) {
                return;
            }
            focusInsideRef.current = false;
            onBlur?.();
        });
    };
    return (jsxs("div", { ref: ref, children: [jsxs("div", { className: styles.phoneInputContainer, onFocus: focusHandler, onBlur: blurHandler, ref: wrapperRefMultiplexer, "data-qa": dataQa, children: [jsx(RegionSelect, { ref: codeInputElementRef, country: country, selectDataProvider: selectDataProvider, selectOptions: selectOptions, searchable: selectOptions.length > 10, callingCode: countryCallingCodeRef.current, onChange: (newCountry) => {
                            let currentPhoneValue = phoneValue;
                            if (country !== newCountry) {
                                setPhoneValue('');
                                currentPhoneValue = '';
                            }
                            setCountry(newCountry);
                            onChangeHandler(newCountry, currentPhoneValue);
                            phoneInputRef.current?.focus();
                        }, availableCountries: availableCountries, priorityCountries: priorityCountries, libPhoneNumberAPI: libPhoneNumberAPI, invalid: invalid, disabled: isComponentDisabled, metadataVariant: metadataVariant }), jsx(Input, { ref: inputRefMultiplexer, size: "large", onChange: (value) => {
                            setPhoneValue(value);
                            onChangeHandler(country, value);
                        }, formatter: phoneFormatter, value: phoneValue, invalid: invalid, disabled: isComponentDisabled, inputMode: "tel", "data-qa": "magritte-phone-input-national-number-input" })] }), jsx(FormHelper, { invalid: invalid, description: description, disabled: isComponentDisabled, errorMessage: errorMessage, "data-qa": "magritte-phone-input-form-helper" })] }));
});
PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
//# sourceMappingURL=PhoneInput.js.map
